import React, { useState, useMemo, useCallback } from "react";
import Loader from "../../Components/Loader/Loader";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { closeDrawer } from "../../actions/drawerActions";
import { portfolioAdded } from "../../actions/portfolioActions";
import "./AddClientPortfolioLineItems.scss";
import { ClientDepthManualAddApi } from "../../Apis/ClientDepthManualAdd";
import { handleApiError, showNotification } from "../../actions/notificationActions";
import Toaster from "../../Components/Toaster/Toaster";
import TagManager from "react-gtm-module";
import AutoCompleteTicker from "../../Components/AutoCompleteTicker/AutoCompleteTicker";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OptionExpirationDatePicker from "../OptionExpirationDatePicker/OptionExpirationDatePicker";
import { fetchOptionsChainTickers } from "../../actions/optionChainTickersAction";

const PORTFOLIO_NAME_REGEX = /^[A-Za-z]+[A-Za-z0-9!@#$%^&*()_+{}[\]:;<>,.?~\\-\s]*$/;
const TICKER_REGEX = /^[A-Za-z\-.$^&]+$/;
const QUANTITY_REGEX = /^-?[0-9]\d*\.?\d*$/;

function ClientPortfolioLineItemsManualImport({ onSubmitRedirectRoute }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const disableSubmit = false;
  const [clientName, setClientName] = useState("Client 001");
  const [externalId, setExternalId] = useState("");
  const [clientPortfolios, setClientPortfolios] = useState([
    {
      id: `initial_${Date.now()}`,
      portfolio_name: "Portfolio 001",
      account_id: "",
      line_items: [],
    },
  ]);
  const [clientPortfolioErrors, setClientPortfolioErrors] = useState([
    {
      portfolio_name: "",
      line_items: [],
    },
  ]);
  const [clientNameError, setClientNameError] = useState("");
  const [toggleOptionTickerFlag, setToggleOptionTickerFlag] = useState(false);
  const allAvailableTickers = useSelector(
    (state) => state.availableTickers.availableTickers
  );
  const allAvailableOptionTickers = useSelector(
    (state) => state.availableOptionsTickers.availableOptionsTickers
  );
  const optionChainTickers = useSelector(state => state.optionChainTickers);
  const { customers } = useSelector(state => state.portfolio);
  
  const totalPortfolioCount = useMemo(() => {
    return customers.reduce((total, customer) => {
      return total + (Array.isArray(customer.Portfolio) ? customer.Portfolio.length : 0);
    }, 0);
  }, [customers]);
  
  const subscriptionsDetails = useSelector(state => state.userSubscription.subscriptionData) || {};
  const { user_type: userType, customer_id: CustomerID } = subscriptionsDetails;

  const getLineItemOptionData = useCallback((portfolioIndex, lineItemIndex) => {
    const lineItem = clientPortfolios[portfolioIndex]?.line_items[lineItemIndex];
    if (!lineItem || !lineItem.optionTicker) return [];
    return optionChainTickers.optionsChainTickers[lineItem.optionTicker] || [];
  }, [clientPortfolios, optionChainTickers.optionsChainTickers]);

  const getAvailableContractTypes = useCallback((portfolioIndex, lineItemIndex) => {
    const optionData = getLineItemOptionData(portfolioIndex, lineItemIndex);
    if (!optionData.length) return [];
    const contractTypes = [...new Set(optionData.map(option => option.call_put))];
    return contractTypes.sort();
  }, [getLineItemOptionData]);

  const getAvailableStrikePrices = useCallback((portfolioIndex, lineItemIndex) => {
    const lineItem = clientPortfolios[portfolioIndex]?.line_items[lineItemIndex];
    const optionData = getLineItemOptionData(portfolioIndex, lineItemIndex);
    if (!optionData.length || !lineItem?.contractType || !lineItem?.expirationDate) return [];
    const strikePrices = optionData
      .filter(option => option.call_put === lineItem.contractType &&
        option.expiration_date === lineItem.expirationDate
      )
      .map(option => option.strike_price)
      .filter((price, index, arr) => arr.indexOf(price) === index)
      .sort((a, b) => parseFloat(a) - parseFloat(b));
    return strikePrices;
  }, [getLineItemOptionData, clientPortfolios]);

  const getAvailableExpirationDates = useCallback((portfolioIndex, lineItemIndex) => {
    const lineItem = clientPortfolios[portfolioIndex]?.line_items[lineItemIndex];
    const optionData = getLineItemOptionData(portfolioIndex, lineItemIndex);
    if (!optionData.length || !lineItem?.contractType) return [];
    const expirationDates = optionData
      .filter(option => 
        option.call_put === lineItem.contractType
      )
      .map(option => option.expiration_date)
      .filter((date, index, arr) => arr.indexOf(date) === index)
      .sort();
    return expirationDates;
  }, [getLineItemOptionData, clientPortfolios]);

  const isOptionSymbol = useCallback((symbol) => {
    if (!symbol) return false;
    const optionPattern = /^[A-Z]+\d{4}-\d{2}-\d{2}[CP]\d+\.?\d*$/;
    const result = optionPattern.test(symbol);
    return result;
  }, []);

  const validateOptionSymbol = useCallback((portfolioIndex, lineItemIndex, symbol) => {
    let validFlag = true;
    let error = [...clientPortfolioErrors];
    
    if (!symbol) {
      error[portfolioIndex].line_items[lineItemIndex].symbol = "Please enter option symbol";
      validFlag = false;
    } else if (!isOptionSymbol(symbol)) {
      error[portfolioIndex].line_items[lineItemIndex].symbol = "Invalid option symbol format";
      validFlag = false;
    } else if (clientPortfolios[portfolioIndex].line_items.filter((item, index) => 
      item.symbol === symbol && index !== lineItemIndex
    ).length > 0) {
      error[portfolioIndex].line_items[lineItemIndex].symbol = "This symbol has already been added";
      validFlag = false;
    } else {
      error[portfolioIndex].line_items[lineItemIndex].symbol = "";
      validFlag = true;
    }
    
    setClientPortfolioErrors([...error]);
    return validFlag;
  }, [clientPortfolioErrors, clientPortfolios, isOptionSymbol]);

  const isQuantityFieldEnabled = useCallback((portfolioIndex, lineItemIndex) => {
    const lineItem = clientPortfolios[portfolioIndex]?.line_items[lineItemIndex];
    const errors = clientPortfolioErrors[portfolioIndex]?.line_items[lineItemIndex];
    if (!lineItem || !errors) return false;
    if (lineItem.ticker_type === 'option') {
      const hasAllFields = lineItem.optionTicker && lineItem.contractType && lineItem.strikePrice && lineItem.expirationDate;
      const hasNoErrors = !errors.symbol && !errors.contractType && !errors.strikePrice && !errors.expirationDate;
      return hasAllFields && hasNoErrors;
    } else {
      const hasSymbol = lineItem.symbol;
      const hasNoSymbolError = !errors.symbol;
      return hasSymbol && hasNoSymbolError;
    }
  }, [clientPortfolios, clientPortfolioErrors]);

  const checkAddPortfolioLineItems = useCallback((portfolioLineItemsCount, action) => {
    const limit = subscriptionsDetails.subscription_plan_active_portfolio_line_items_limit;
    if (limit === 'Infinity') {
      return true;
    }
    const exceedsLimit = action === 'submit' ? portfolioLineItemsCount > limit: portfolioLineItemsCount >= limit;
    if (exceedsLimit) {
      const message = `Your current portfolio line items count (${portfolioLineItemsCount}) exceeds the limit of ${limit}. Please upgrade your subscription to add more.`;
      dispatch(showNotification(message, "info", "toaster"));
      return false;
    }
    return true;
  }, [subscriptionsDetails.subscription_plan_active_portfolio_line_items_limit, dispatch]);

  const handleAddLineItem = useCallback((portfolioIndex) => {
    const validPliCount = clientPortfolioErrors[portfolioIndex]?.line_items?.filter(error =>
      error.symbol === '' || (error.symbol && error.symbol !== "Ticker Not Recognized")
    ).length || 0;
    if(checkAddPortfolioLineItems(validPliCount,'add')){
      const updatedPortfolioLineItems = [...clientPortfolios];
      const uniqueId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      updatedPortfolioLineItems[portfolioIndex].line_items.push({
        id: uniqueId,
        ticker_type: "stock",
        symbol: "",
        quantity: "",
        contractType: "",
        strikePrice: "",
        expirationDate: "",
        optionTicker: "",
      });
      setClientPortfolios([...updatedPortfolioLineItems]);
      const portfolioLineItemsErrors = [...clientPortfolioErrors];
      portfolioLineItemsErrors[portfolioIndex].line_items.push({
        symbol: "",
        quantity: "",
        contractType: "",
        strikePrice: "",
        expirationDate: "",
      });
      setClientPortfolioErrors([...portfolioLineItemsErrors]);
    }
  }, [clientPortfolioErrors, checkAddPortfolioLineItems, clientPortfolios]);

  const handleDeleteLineItem = useCallback((portfolioIndex, lineItemIndex) => {
    const deletedPortfolioLineItems = [...clientPortfolios];
    deletedPortfolioLineItems[portfolioIndex].line_items.splice(
      lineItemIndex,
      1
    );
    setClientPortfolios([...deletedPortfolioLineItems]);
    const deletedPortfolioLineItemsErrors = [...clientPortfolioErrors];
    deletedPortfolioLineItemsErrors[portfolioIndex].line_items.splice(
      lineItemIndex,
      1
    );
    setClientPortfolioErrors([...deletedPortfolioLineItemsErrors]);
  }, [clientPortfolios, clientPortfolioErrors]);

  const checkAddPortfolioLimit = useCallback(() => {
    const limit = subscriptionsDetails.subscription_plan_active_portfolio_limit;
    if (limit === 'Infinity') {
      return true;
    }
    const currentPortfolioCount = totalPortfolioCount + clientPortfolios.length;
    if (currentPortfolioCount >= limit) {
      const message = `Your current portfolio count (${currentPortfolioCount}) exceeds the limit of ${limit}. Please upgrade your subscription to add more.`;
      dispatch(showNotification(message, "info", "toaster"));
      return false;
    }
    return true;
  }, [subscriptionsDetails.subscription_plan_active_portfolio_limit, totalPortfolioCount, clientPortfolios.length, dispatch]);

  const handleAddPortfolio = useCallback(() => {
    if (checkAddPortfolioLimit()) {
      const existingNumbers = clientPortfolios
        .map(portfolio => {
          const match = portfolio.portfolio_name.match(/Portfolio (\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        })
        .filter(num => num > 0);
      let nextPortfolioNumber = 1;
      while (existingNumbers.includes(nextPortfolioNumber)) {
        nextPortfolioNumber++;
      }
      const nextPortfolioName = `Portfolio ${String(nextPortfolioNumber).padStart(3, "0")}`;
      const uniqueId = `portfolio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setClientPortfolios(prevPortfolios => [
        ...prevPortfolios,
        {
          id: uniqueId,
          portfolio_name: nextPortfolioName,
          account_id: "",
          line_items: [],
        },
      ]);
      setClientPortfolioErrors(prevErrors => [
        ...prevErrors,
        {
          portfolio_name: "",
          line_items: [],
        },
      ]);
    }
  }, [checkAddPortfolioLimit, clientPortfolios]);
  
  const validateClientName = useCallback((clientName) => {
    let error = "";
    let validFlag = true;
    if (!clientName) {
      validFlag = false;
      error = "Client Name cannot be empty.";
    }
    setClientNameError(error);
    return validFlag;
  }, []);

  const handleClientNameChange = useCallback((clientName) => {
    validateClientName(clientName);
    setClientName(clientName);
  }, [validateClientName]);

  const validatePortfolioName = useCallback((portfolio_name, portfolio_index) => {
    let error = [...clientPortfolioErrors];
    let validFlag = true;
    if (!portfolio_name) {
      validFlag = false;
      error[portfolio_index].portfolio_name = "Please enter portfolio name";
    } else {
      if (
        portfolio_name &&
        !PORTFOLIO_NAME_REGEX.test(
          portfolio_name
        )
      ) {
        validFlag = false;
        error[portfolio_index].portfolio_name = "Invalid Portfolio Name";
      } else {
        error[portfolio_index].portfolio_name = "";
        validFlag = true;
      }
    }
    setClientPortfolioErrors([...error]);
    return validFlag;
  }, [clientPortfolioErrors]);

  const handlePortfolioNameChange = useCallback((e, portfolioIndex) => {
    const portfolio_name = e.target.value.trimLeft();
    validatePortfolioName(portfolio_name, portfolioIndex);
    const updatedPortfolioNames = [...clientPortfolios];
    updatedPortfolioNames[portfolioIndex].portfolio_name = portfolio_name;

    const currentTime = Math.floor(Date.now() / 1000);
    const uniqueId = `${currentTime}_${portfolioIndex}`;
    updatedPortfolioNames[portfolioIndex].account_id = uniqueId;
    setClientPortfolios([...updatedPortfolioNames]);
  }, [clientPortfolios, validatePortfolioName]);

  const handleDeletePortfolio = useCallback((portfolioIndex) => {
    const deletedPortfolios = [...clientPortfolios];
    deletedPortfolios.splice(portfolioIndex, 1);
    setClientPortfolios([...deletedPortfolios]);
    const deletedPortfolioErrors = [...clientPortfolioErrors];
    deletedPortfolioErrors.splice(portfolioIndex, 1);
    setClientPortfolioErrors([...deletedPortfolioErrors]);
  }, [clientPortfolios, clientPortfolioErrors]);

  const validateSymbol = useCallback((portfolioIndex, lineItemIndex, ticker) => {
    let validFlag = true;
    let error = [...clientPortfolioErrors];
    if (!ticker) {
      error[portfolioIndex].line_items[lineItemIndex].symbol =
        "Please enter ticker";
      validFlag = false;
    } else {
      const referenceTicker = allAvailableTickers.find(
        (refTicker) => refTicker.symbol === ticker
      );
      if (ticker && !TICKER_REGEX.test(ticker)) {
        error[portfolioIndex].line_items[lineItemIndex].symbol = "Invalid Ticker";
        validFlag = false;
      } else if (!referenceTicker) {
        error[portfolioIndex].line_items[lineItemIndex].symbol = "Ticker Not Recognized";
        validFlag = false;
      } else if(clientPortfolios[portfolioIndex].line_items.filter((item, index) => 
        item.symbol === ticker && index !== lineItemIndex
      ).length > 0){
        error[portfolioIndex].line_items[lineItemIndex].symbol = "This ticker has already been added";
        validFlag = false;
      } else {
        error[portfolioIndex].line_items[lineItemIndex].symbol = "";
        validFlag = true;
      }
    }
    setClientPortfolioErrors([...error]);
    return validFlag;
  }, [clientPortfolioErrors, allAvailableTickers, clientPortfolios]);

  const validateQuantity = useCallback((portfolioIndex, lineItemIndex, quantity) => {
    let validFlag = true;
    let error = [...clientPortfolioErrors];
    if (!quantity) {
      error[portfolioIndex].line_items[lineItemIndex].quantity = "Please enter quantity";
      validFlag = false;
    } else {
      if (quantity && !QUANTITY_REGEX.test(quantity)) {
        error[portfolioIndex].line_items[lineItemIndex].quantity = "Please enter valid quantity";
        validFlag = false;
      } else {
        error[portfolioIndex].line_items[lineItemIndex].quantity = "";
        validFlag = true;
      }
    }
    setClientPortfolioErrors([...error]);
    return validFlag;
  }, [clientPortfolioErrors]);

  const validateOptionDuplication = useCallback((optionTicker, contractType, strikePrice, expirationDate, portfolioIndex, lineItemIndex) => {
    const errors = [...clientPortfolioErrors];
    if (optionTicker && contractType && strikePrice && expirationDate) {
      const isDuplicate = clientPortfolios[portfolioIndex].line_items.some((item, index) => {
        return index !== lineItemIndex &&
          item.ticker_type === 'option' &&
          item.optionTicker === optionTicker &&
          item.contractType === contractType &&
          item.strikePrice === strikePrice &&
          item.expirationDate === expirationDate;
      });
      if (isDuplicate) {
        errors[portfolioIndex].line_items[lineItemIndex].symbol = "This option contract has already been added";
        setClientPortfolioErrors(errors);
        return false;
      } else {
          if (errors[portfolioIndex].line_items[lineItemIndex].symbol === "This option contract has already been added") {
            errors[portfolioIndex].line_items[lineItemIndex].symbol = "";
            setClientPortfolioErrors(errors);
          }
      }
    }
    return true;
  }, [clientPortfolioErrors, clientPortfolios]);

  const validateOptionFields = useCallback((portfolioIndex, lineItemIndex) => {
    const lineItem = clientPortfolios[portfolioIndex].line_items[lineItemIndex];
    const errors = [...clientPortfolioErrors];
    let isValid = true;
    if (!lineItem.optionTicker) {
      errors[portfolioIndex].line_items[lineItemIndex].symbol = "Please enter option ticker";
      isValid = false;
    } else {
      errors[portfolioIndex].line_items[lineItemIndex].symbol = "";
    }
    if (!lineItem.contractType) {
      errors[portfolioIndex].line_items[lineItemIndex].contractType = "Please select contract type";
      isValid = false;
    } else {
      errors[portfolioIndex].line_items[lineItemIndex].contractType = "";
    }
    if (!lineItem.strikePrice) {
      errors[portfolioIndex].line_items[lineItemIndex].strikePrice = "Please select strike price";
      isValid = false;
    } else {
      errors[portfolioIndex].line_items[lineItemIndex].strikePrice = "";
    }
    if (!lineItem.expirationDate) {
      errors[portfolioIndex].line_items[lineItemIndex].expirationDate = "Please select expiration date";
      isValid = false;
    } else {
      errors[portfolioIndex].line_items[lineItemIndex].expirationDate = "";
    }
    setClientPortfolioErrors(errors);
    return isValid;
  }, [clientPortfolios, clientPortfolioErrors]);

  const handleLineItemChange = useCallback((
    value,
    portfolioIndex,
    lineItemIndex,
    property
  ) => {
    const updatedPortfolioLineitems = [...clientPortfolios];
    const lineItem = updatedPortfolioLineitems[portfolioIndex].line_items[lineItemIndex];
    if (property === "ticker_type") {
      lineItem.ticker_type = value;
      lineItem.symbol = "";
      lineItem.quantity = "";
      lineItem.optionTicker = "";
      lineItem.contractType = "";
      lineItem.strikePrice = "";
      lineItem.expirationDate = "";
      const updatedErrors = [...clientPortfolioErrors];
      updatedErrors[portfolioIndex].line_items[lineItemIndex] = {
        symbol: "",
        quantity: "",
        contractType: "",
        strikePrice: "",
        expirationDate: "",
      };
      setClientPortfolioErrors(updatedErrors);
      const hasOptionType = updatedPortfolioLineitems.some(portfolio =>
        portfolio.line_items.some(item => item.ticker_type === 'option')
      );
      setToggleOptionTickerFlag(hasOptionType);
    } else if (property === "symbol") {
      validateSymbol(portfolioIndex, lineItemIndex, value);
      lineItem.symbol = value;
    } else if (property === "quantity") {
      if (isQuantityFieldEnabled(portfolioIndex, lineItemIndex)) {
        validateQuantity(portfolioIndex, lineItemIndex, value);
        lineItem.quantity = value;
      }
    } else if (property === "optionTicker") {
      const tickerSymbol = value ? value.toUpperCase() : "";
      lineItem.optionTicker = tickerSymbol;
      lineItem.contractType = "";
      lineItem.strikePrice = "";
      lineItem.expirationDate = "";
      lineItem.symbol = "";
      lineItem.quantity = "";
      validateOptionDuplication(lineItem.optionTicker, lineItem.contractType, lineItem.strikePrice, lineItem.expirationDate, portfolioIndex, lineItemIndex);
      if (tickerSymbol && allAvailableOptionTickers.find(t => t.symbol === tickerSymbol)) {
        if (!optionChainTickers.optionsChainTickers[tickerSymbol]) {
          dispatch(fetchOptionsChainTickers(tickerSymbol));
        }
      }
    } else if (property === "contractType") {
      lineItem.contractType = value;
      lineItem.expirationDate = "";
      lineItem.strikePrice = "";
      lineItem.symbol = "";
      lineItem.quantity = "";
      validateOptionDuplication(lineItem.optionTicker, lineItem.contractType, lineItem.strikePrice, lineItem.expirationDate, portfolioIndex, lineItemIndex);
    } else if (property === "expirationDate") {
      lineItem.expirationDate = value;
      lineItem.strikePrice = "";
      lineItem.symbol = "";
      lineItem.quantity = "";
      validateOptionDuplication(lineItem.optionTicker, lineItem.contractType, lineItem.strikePrice, lineItem.expirationDate, portfolioIndex, lineItemIndex);
    } else if (property === "strikePrice") {
      lineItem.strikePrice = value;
      lineItem.quantity = "";
      validateOptionDuplication(lineItem.optionTicker, lineItem.contractType, lineItem.strikePrice, lineItem.expirationDate, portfolioIndex, lineItemIndex);
    }
    if (lineItem.ticker_type === 'option' && lineItem.optionTicker && lineItem.contractType && lineItem.strikePrice && lineItem.expirationDate) {
      const optionData = optionChainTickers.optionsChainTickers[lineItem.optionTicker] || [];
      const matchingOption = optionData.find(option => {
        return option.call_put === lineItem.contractType &&
          option.strike_price === parseFloat(lineItem.strikePrice) &&
          option.expiration_date === lineItem.expirationDate;
      });
      if (matchingOption && matchingOption.opt_sec_id) {
        lineItem.symbol = matchingOption.opt_sec_id;
      }
    }
    setClientPortfolios([...updatedPortfolioLineitems]);
  }, [clientPortfolios, clientPortfolioErrors, allAvailableOptionTickers, optionChainTickers, dispatch, isQuantityFieldEnabled, validateOptionDuplication, validateQuantity, validateSymbol]);

  const validateOnSubmit = useCallback(() => {
    let validFlag = true;
    validFlag = validateClientName(clientName);
    if (clientPortfolios.length !== 0) {
      if(!checkAddPortfolioLimit()){
        validFlag = false;
      }
      clientPortfolios.forEach((portfolio, portfolio_index) => {
        if (!validatePortfolioName(portfolio.portfolio_name, portfolio_index)) {
          validFlag = false;
        }
        const validPliCount = clientPortfolioErrors[portfolio_index].line_items?.filter(error =>
          error.symbol === '' || (error.symbol && error.symbol !== "Ticker Not Recognized")
        ).length || 0;
        if(!checkAddPortfolioLineItems(validPliCount,'submit')){
          validFlag = false;
        }
        portfolio.line_items.forEach((lineItem, lineItemIndex) => {
          const isOption = lineItem.ticker_type === 'option' || isOptionSymbol(lineItem.symbol);
          
          if (isOption) {
            if (lineItem.ticker_type === 'option') {
              // This is an option with separate fields, validate them
              if (!validateOptionFields(portfolio_index, lineItemIndex)) {
                validFlag = false;
              }
            } else if (isOptionSymbol(lineItem.symbol)) {
              // This is an option with final symbol format, validate the option symbol
              if (!validateOptionSymbol(portfolio_index, lineItemIndex, lineItem.symbol)) {
                validFlag = false;
              }
            }
          } else {
            // This is a stock symbol
            if (
              !validateSymbol(portfolio_index, lineItemIndex, lineItem.symbol)
            ) {
              validFlag = false;
            }
          }
          if (
            !validateQuantity(portfolio_index, lineItemIndex, lineItem.quantity)
          ) {
            validFlag = false;
          }
        });
      });
    }
    return validFlag;
  }, [validateClientName, clientName, clientPortfolios, checkAddPortfolioLimit, validatePortfolioName, clientPortfolioErrors, checkAddPortfolioLineItems, validateOptionFields, validateSymbol, validateQuantity, isOptionSymbol, validateOptionSymbol]);

  const handleOnSubmit = async () => {
    const filteredPortfolios = clientPortfolios.map(portfolio => ({
      portfolio_name: portfolio.portfolio_name,
      account_id: portfolio.account_id,
      line_items: portfolio.line_items.map(lineItem => ({
        symbol: lineItem.symbol,
        quantity: lineItem.quantity
      }))
    }));

    const clientData = {
      full_name: clientName,
      external_id: externalId,
      portfolios: filteredPortfolios,
    };
    if (validateOnSubmit()) {
      setIsLoading(true);
      const response = await ClientDepthManualAddApi(clientData);
      if (response.status === 200) {
        dispatch(portfolioAdded());
        dispatch(closeDrawer());
        if (onSubmitRedirectRoute) {
          TagManager.dataLayer({
            dataLayer: {
              event: onSubmitRedirectRoute
                ? "Onboard Client Portfolio"
                : "Client Data Added Manually",
              userType: userType,
              user_id: CustomerID,
              onboarding_type: "Manually",
            },
          });
          history.push(onSubmitRedirectRoute);
        }
      } else {
        handleApiError(response, dispatch);
      }
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="toast-container">
        <Toaster />
      </div>
      <div className="client-portfolio">
        {isLoading ? (
          <div className="loading-overlay">
            <Loader />
          </div>
        ) : (
          <>
            <form className="ai-row">
              <div className="ai-form-group">
                <label className="ai-form-label">Client Name:</label>
                <input
                  type="text"
                  className="ai-form-value"
                  value={clientName}
                  onChange={(e) => handleClientNameChange(e.target.value)}
                />
                <p className="ai-error-message">{clientNameError}</p>
              </div>
              <div className="ai-form-group">
                <label className="ai-form-label">External ID (Optional):</label>
                <input
                  type="text"
                  className="ai-form-value"
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                />
              </div>
            </form>
            <h2 className="ai-title">Portfolios</h2>
            {clientPortfolios.map((portfolio, portfolioIndex) => (
              <div key={portfolio.id || `fallback-portfolio-${portfolioIndex}`} className="portfoliocard">
                <div className="portfolioheader">
                  <div className="input">
                    <input
                      type="text"
                      className="ai-form-value"
                      placeholder="Portfolio Name"
                      value={portfolio.portfolio_name}
                      onChange={(e) =>
                        handlePortfolioNameChange(e, portfolioIndex)
                      }
                    />
                    <p className="ai-error-message">
                      {clientPortfolioErrors[portfolioIndex].portfolio_name}
                    </p>
                  </div>
                  <button onClick={() => handleDeletePortfolio(portfolioIndex)}>
                    <img src="./Assets/trash.svg" alt="delete" />
                  </button>
                </div>
                <div className="tickercon">
                  <div className="tickerlist">
                    {portfolio.line_items.length > 0 &&
                      portfolio.line_items.map((lineItem, lineItemIndex) => (
                        <div key={lineItem.id || `fallback-${portfolioIndex}-${lineItemIndex}`} className="addticker-wrapper">
                          <div className="title-add-ticker">
                            <div className="switchcontent">
                              Add Stock
                              <div className="toggle-switch-small">
                                <input
                                  type="checkbox"
                                  id={`ticker-toggle-${portfolioIndex}-${lineItemIndex}`}
                                  checked={lineItem.ticker_type === 'option'}
                                  onChange={(e) => 
                                    handleLineItemChange(
                                      e.target.checked ? 'option' : 'stock',
                                      portfolioIndex,
                                      lineItemIndex,
                                      "ticker_type"
                                    )
                                  }
                                />
                                <label 
                                  htmlFor={`ticker-toggle-${portfolioIndex}-${lineItemIndex}`} 
                                  className="toggle-label-small"
                                ></label>
                              </div>
                              Add Option
                            </div>
                          </div>
                          {lineItem.ticker_type === 'option' ? (
                            <div className="addOptionticker">
                              <div className="tickerdata">
                                <AutoCompleteTicker
                                  key={`option-${lineItem.id || `${portfolioIndex}-${lineItemIndex}`}-${lineItem.ticker_type}`}
                                  onTickerChange={(event, newValue) => {
                                    const tickerSymbol = newValue ? newValue.symbol : "";
                                    handleLineItemChange(tickerSymbol, portfolioIndex, lineItemIndex, "optionTicker");
                                  }}
                                  toggleOptionTickerFlag={toggleOptionTickerFlag}
                                />
                                <p className="ai-error-message">
                                  {clientPortfolioErrors[portfolioIndex].line_items[lineItemIndex].symbol}
                                </p>
                              </div>
                              <div className="tickerdata">
                                <Select
                                  value={lineItem.contractType || ""}
                                  onChange={(e) => 
                                    handleLineItemChange(
                                      e.target.value,
                                      portfolioIndex,
                                      lineItemIndex,
                                      "contractType"
                                    )
                                  }
                                  displayEmpty
                                  size="small"
                                  disabled={!lineItem.optionTicker || optionChainTickers.isLoadingChain}
                                  className="ai-backtest-select"
                                >
                                  <MenuItem value="" disabled>Contract Type</MenuItem>
                                  {getAvailableContractTypes(portfolioIndex, lineItemIndex).map((type) => (
                                    <MenuItem key={type} value={type}>
                                      {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <p className="ai-error-message">
                                  {clientPortfolioErrors[portfolioIndex].line_items[lineItemIndex].contractType}
                                </p>
                              </div>
                              <div className="tickerdata">
                                <OptionExpirationDatePicker
                                  defaultDate={lineItem.expirationDate || ""}
                                  onDateSelect={(date) => 
                                    handleLineItemChange(
                                      date,
                                      portfolioIndex,
                                      lineItemIndex,
                                      "expirationDate"
                                    )
                                  }
                                  availableDates={getAvailableExpirationDates(portfolioIndex, lineItemIndex)}
                                  disabled={!lineItem.optionTicker || optionChainTickers.isLoadingChain || !lineItem.contractType}
                                  placeholder="Expiration Date"
                                />
                                <p className="ai-error-message">
                                  {clientPortfolioErrors[portfolioIndex].line_items[lineItemIndex].expirationDate}
                                </p>
                              </div>
                              <div className="tickerdata">
                                <Select
                                  value={lineItem.strikePrice || ""}
                                  onChange={(e) => 
                                    handleLineItemChange(
                                      e.target.value,
                                      portfolioIndex,
                                      lineItemIndex,
                                      "strikePrice"
                                    )
                                  }
                                  displayEmpty
                                  size="small"
                                  disabled={!lineItem.optionTicker || optionChainTickers.isLoadingChain || !lineItem.contractType || !lineItem.expirationDate}
                                  className="ai-backtest-select"
                                >
                                  <MenuItem value="" disabled>Strike Price</MenuItem>
                                  {getAvailableStrikePrices(portfolioIndex, lineItemIndex).map((price) => (
                                    <MenuItem key={price} value={price}>
                                      ${price}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <p className="ai-error-message">
                                  {clientPortfolioErrors[portfolioIndex].line_items[lineItemIndex].strikePrice}
                                </p>
                              </div>
                              <div className="tickerdata">
                                <input
                                  className="ai-form-value"
                                  type="text"
                                  placeholder="Quantity"
                                  value={lineItem.quantity}
                                  onChange={(e) =>
                                    handleLineItemChange(
                                      e.target.value,
                                      portfolioIndex,
                                      lineItemIndex,
                                      "quantity"
                                    )
                                  }
                                  disabled={!isQuantityFieldEnabled(portfolioIndex, lineItemIndex)}
                                />
                                <p className="ai-error-message">
                                  {clientPortfolioErrors[portfolioIndex].line_items[lineItemIndex].quantity}
                                </p>
                              </div>
                              <button
                                onClick={() =>
                                  handleDeleteLineItem(
                                    portfolioIndex,
                                    lineItemIndex
                                  )
                                }
                              >
                                <img src="./Assets/trash.svg" alt="delete" />
                              </button>
                              {optionChainTickers.isLoadingChain && 
                               lineItem.optionTicker && 
                               !optionChainTickers.optionsChainTickers[lineItem.optionTicker] && (
                                <p className="option-loading-message">
                                  Fetching Option Contracts...
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="addticker">
                              <div className="tickerdata">
                                <AutoCompleteTicker
                                  key={`stock-${lineItem.id || `${portfolioIndex}-${lineItemIndex}`}-${lineItem.ticker_type}`}
                                  onTickerChange={(event, newValue) => {
                                    const tickerSymbol = newValue ? newValue.symbol : "";
                                    handleLineItemChange(tickerSymbol, portfolioIndex, lineItemIndex, "symbol");
                                  }}
                                  toggleOptionTickerFlag={toggleOptionTickerFlag}
                                />
                                <p className="ai-error-message">
                                  {clientPortfolioErrors[portfolioIndex].line_items[lineItemIndex].symbol}
                                </p>
                              </div>
                              <div className="tickerdata">
                                <input
                                  className="ai-form-value"
                                  type="text"
                                  placeholder="Quantity"
                                  value={lineItem.quantity}
                                  onChange={(e) =>
                                    handleLineItemChange(
                                      e.target.value,
                                      portfolioIndex,
                                      lineItemIndex,
                                      "quantity"
                                    )
                                  }
                                  disabled={!isQuantityFieldEnabled(portfolioIndex, lineItemIndex)}
                                />
                                <p className="ai-error-message">
                                  {clientPortfolioErrors[portfolioIndex].line_items[lineItemIndex].quantity}
                                </p>
                              </div>
                              <button
                                onClick={() =>
                                  handleDeleteLineItem(
                                    portfolioIndex,
                                    lineItemIndex
                                  )
                                }
                              >
                                <img src="./Assets/trash.svg" alt="delete" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                  <span className="addticker">
                    <button onClick={() => handleAddLineItem(portfolioIndex)}>
                      +
                    </button>
                  </span>
                </div>
              </div>
            ))}
            <div className="ai-row">
              <button
                className="ai-btn solid blue"
                onClick={handleAddPortfolio}
              >
                Add More Portfolios
              </button>
              <button
                className="ai-btn solid primary"
                onClick={handleOnSubmit}
                disabled={disableSubmit}
              >
                Submit
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default ClientPortfolioLineItemsManualImport;
